import { ComponentProps, CSSProperties, HTMLAttributes, memo, MouseEvent, PointerEvent, TouchEvent } from 'react';
import clsx from 'clsx';
import type { RowData } from '@tanstack/react-table';
import { HiOutlineMapPin } from 'react-icons/hi2';
import type { DataTableAlign, IDataTableColumn } from '../DataTable.types';
import type { DataTableColumn } from '../dataTableFeatures';
import DataTableHeaderFilter from './DataTableHeaderFilter';
import DataTableSortMark from './DataTableSortMark';

/** События заголовка — из корня таблицы, через ref: ячейка шапки рисуется только при смене своего состояния, а
 *  обработчики зовутся свежими. */
export interface IDataTableHeaderCellRenderer<T extends RowData> {
    toggleSort: (column: DataTableColumn<T>, e: unknown) => void;
    openMenu: (column: DataTableColumn<T>, e: MouseEvent) => void;
    /** Перенос колонки (draggable, dragstart/dragover/drop): обработчики держат только ref-ы корня. */
    reorderProps: (
        column: DataTableColumn<T>,
    ) => Pick<
        HTMLAttributes<HTMLElement>,
        'draggable' | 'onPointerDownCapture' | 'onDragStart' | 'onDragEnd' | 'onDragOver' | 'onDragLeave' | 'onDrop'
    >;
    startResize: (column: DataTableColumn<T>, e: PointerEvent<HTMLElement>) => void;
    touchResize: (column: DataTableColumn<T>, e: TouchEvent<HTMLElement>) => void;
}

type HeaderFilterProps = ComponentProps<typeof DataTableHeaderFilter>;

export interface IDataTableHeaderCellProps<T extends RowData> {
    column: DataTableColumn<T>;
    col: IDataTableColumn<T>;
    /** `aria-colindex`: с единицы. */
    colIndex: number;
    align: DataTableAlign;
    sorted: 'asc' | 'desc' | false;
    canSort: boolean;
    /** Уровень сортировки колонки, с единицы; виден при сортировке по нескольким колонкам. */
    sortIndex: number;
    multiSort: boolean;
    pinned: 'start' | 'end' | false;
    /** Фиксированная узкая колонка: без номера уровня сортировки (см. SCSS). */
    tight: boolean;
    stickyClassName: string | undefined;
    style: CSSProperties | undefined;
    highlighted: boolean;
    reorderable: boolean;
    /** Воронка фильтра колонки; без неё остальные `filter*` не читаются. */
    filterShown: boolean;
    filterSelected: HeaderFilterProps['selected'];
    filterScope: HeaderFilterProps['scope'];
    getFacet: HeaderFilterProps['getFacet'];
    filterLoadPage: HeaderFilterProps['loadPage'];
    filterServerSearch: HeaderFilterProps['serverSearch'];
    onFilterChange: HeaderFilterProps['onChange'];
    resizable: boolean;
    resizing: boolean;
    renderer: { readonly current: IDataTableHeaderCellRenderer<T> };
}

function DataTableHeaderCell<T extends RowData>({
    column,
    col,
    colIndex,
    align,
    sorted,
    canSort,
    sortIndex,
    multiSort,
    pinned,
    tight,
    stickyClassName,
    style,
    highlighted,
    reorderable,
    filterShown,
    filterSelected,
    filterScope,
    getFacet,
    filterLoadPage,
    filterServerSearch,
    onFilterChange,
    resizable,
    resizing,
    renderer,
}: IDataTableHeaderCellProps<T>) {
    const hintNode = col.headerHint ? (
        <span
            className="dtb__hhint"
            // у перенесённого заголовка значок внутри текста с title — пустой title гасит его
            // системную подсказку поверх своей
            title=""
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
        >
            {col.headerHint}
        </span>
    ) : null;
    return (
        <div
            className={clsx(
                'dtb__hcell',
                // фиксированная узкая колонка: без номера уровня сортировки,
                // индикаторам место уступает текст (см. SCSS)
                tight && 'dtb__hcell--tight',
                canSort && 'dtb__hcell--sortable',
                align !== 'left' && `dtb__hcell--${align}`,
                sorted && 'is-sorted',
                pinned && 'dtb__hcell--pin',
                pinned === 'start' && 'dtb__stick-l',
                pinned === 'end' && 'dtb__stick-r',
                stickyClassName,
                highlighted && 'is-highlighted',
            )}
            // по нему ресайз считывает фактические ширины колонок
            // (data-col занят ячейками строк)
            data-head-col={column.id}
            style={style}
            role="columnheader"
            aria-label={col.headerContent ? col.header : undefined}
            aria-colindex={colIndex}
            aria-sort={sorted === 'asc' ? 'ascending' : sorted === 'desc' ? 'descending' : canSort ? 'none' : undefined}
            tabIndex={canSort ? 0 : undefined}
            onClick={(e) => renderer.current.toggleSort(column, e)}
            onContextMenu={(e) => renderer.current.openMenu(column, e)}
            onKeyDown={
                canSort
                    ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              renderer.current.toggleSort(column, e);
                          }
                      }
                    : undefined
            }
            {...(reorderable ? renderer.current.reorderProps(column) : undefined)}
        >
            <span className="dtb__hcontent">
                {pinned && <HiOutlineMapPin className="dtb__hpin" aria-hidden />}
                {col.headerContent ? (
                    <span className="dtb__hcustom">{col.headerContent}</span>
                ) : (
                    <span
                        className={clsx(
                            'dtb__htext',
                            reorderable && 'dtb__htext--drag',
                            col.headerNoEllipsis && 'dtb__htext--nowrapoff',
                        )}
                        title={col.header}
                    >
                        {col.hideHeaderText ? '' : col.header}
                        {/* перенесённый заголовок: значок — за последним словом, а не у
                            правого края блока строк */}
                        {col.headerNoEllipsis && hintNode && (
                            <>
                                {'⁠'}
                                {hintNode}
                            </>
                        )}
                    </span>
                )}
                {!(col.headerNoEllipsis && !col.headerContent) && hintNode}
                {/* в покое видимы только АКТИВНЫЕ состояния: стрелка действующей
                    сортировки и залитая воронка действующего фильтра — остальное
                    появляется в том же месте потока при наведении (см. SCSS),
                    поэтому позиция индикаторов не меняется между состояниями */}
                {canSort && <DataTableSortMark active={!!sorted} dir={sorted} index={sortIndex} multi={multiSort} />}
                {filterShown || col.headerExtra ? (
                    <span className="dtb__htools">
                        {filterShown && (
                            <DataTableHeaderFilter
                                columnId={column.id}
                                header={col.header}
                                selected={filterSelected}
                                getFacet={getFacet}
                                loadPage={filterLoadPage}
                                serverSearch={filterServerSearch}
                                scope={filterScope}
                                onChange={onFilterChange}
                            />
                        )}
                        {col.headerExtra && (
                            // индикаторы шапки (тултип/мультиредактирование) — клики
                            // не должны долетать до сортировки заголовка
                            <span
                                className="dtb__hextra"
                                onClick={(e) => e.stopPropagation()}
                                onKeyDown={(e) => e.stopPropagation()}
                            >
                                {col.headerExtra}
                            </span>
                        )}
                    </span>
                ) : null}
            </span>
            {resizable && (
                <span
                    className={clsx('dtb__resize', resizing && 'is-resizing')}
                    draggable={false}
                    onPointerDown={(e) => renderer.current.startResize(column, e)}
                    onTouchStart={(e) => renderer.current.touchResize(column, e)}
                    onClick={(e) => e.stopPropagation()}
                    aria-hidden
                />
            )}
        </div>
    );
}

/** Ячейка заголовка колонки. Состояние приходит пропсами, обработчики — через `renderer`: операции таблицы
 *  (правка, добавление строки, выход в просмотр) шапку на сотни колонок не перерисовывают. */
export default memo(DataTableHeaderCell) as typeof DataTableHeaderCell;
